import React, { useState } from "react";
import PropTypes from "prop-types";
import { theme } from "../theme"; // Make sure to import your theme
import { Box, Flex } from "./layout";
import { InputWrapper } from "./input.style";



export const InputField = ({
  label,
  required,
  placeholder = "",
  suffix,
  prefix,
  variant = "outlined", // Default to 'outlined'
  size = "md", // Default size is medium
  labelBold,
  error = false,
  helperText = "",
  disabled = false,
  fullWidth = false,
  valueSinc = false,
  color = "default", // Accepts 'primary', 'secondary', or 'success'
  type = "text", // Set default input type
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState("");

  // Determine colors based on the color prop
  const mainColor = theme.colors[color]?.main || theme.colors.default.dark;
  const errorColor = theme.colors.danger.main;

  return (
    <InputWrapper
      label={label}
      variant={variant}
      error={error}
      disabled={disabled}
      mainColor={mainColor}
      errorColor={errorColor}
      isFocused={focused} // Pass focus state to InputWrapper
      fullWidth={fullWidth}
      valueSinc={valueSinc}
      size={size} // Pass size prop to InputWrapper
      suffix={suffix}
      labelBold={labelBold}
    >
      {suffix && (
        <Flex className="suffix" alignItem="center" justifyContent="center">
          {suffix}
        </Flex>
      )}
      {prefix && (
        <Flex className="prefix" alignItem="center" justifyContent="center">
          {prefix}
        </Flex>
      )}
      <input
        min={0}
        type={type} // Use the type prop here
        value={value} // Controlled input value
        placeholder={
          variant === "standard"
            ? placeholder
            : label
              ? focused && !value && !valueSinc
                ? placeholder
                : ""
              : !value
                ? placeholder
                : ""
        } // Conditional placeholder
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => setValue(e.target.value)} // Update the value state
        {...props}
        className={valueSinc && "asd"}
        name={label}
        id={label}
        autoComplete="off"
      />
      {label && (
        <label htmlFor={label}
          className={`${focused || value || valueSinc ? "focused" : ""} ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}
        >
          {label} {required && <Box className="required">*</Box>}
        </label>
      )}
      <fieldset
        className={`${focused || value || valueSinc ? "focused" : ""} ${error ? "error" : ""} ${!label && "noLabel"}`}
      >
        {label && (
          <legend>
            <span>
              {label}
              {required && (
                <span
                  style={{
                    color: theme.colors.danger.main,
                    paddingInlineEnd: 0,
                  }}
                />
              )}
            </span>
          </legend>
        )}
      </fieldset>
      {helperText && <Box className="helperText">{helperText}</Box>}
    </InputWrapper>
  );
};

// Adding PropTypes for validation
InputField.propTypes = {
  /** Label for the input field */
  label: PropTypes.string.isRequired,

  /** Placeholder text when input is empty */
  placeholder: PropTypes.string,
  /** Variant style: outlined, filled, or standard */
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  /** Size of the input field */
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  /** Error state of the input field */
  error: PropTypes.bool,
  /** Helper text below the input field */
  helperText: PropTypes.string,
  /** Disable the input field */
  disabled: PropTypes.bool,
  /** If true, input will take full width */
  fullWidth: PropTypes.bool,
  valueSinc: PropTypes.bool,
  /** Color theme of the input field */
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "default",
  ]),
  /** Input type (text, password, email, etc.) */
  type: PropTypes.string,
  /** If true, the input is required */
  required: PropTypes.bool,
};

// Default props
InputField.defaultProps = {
  label: "",
  placeholder: "",
  variant: "outlined",
  size: "md", // Default to medium size
  error: false,
  helperText: "",
  disabled: false,
  fullWidth: false,
  color: "primary",
  type: "text",
  valueSinc: false,
  required: false,
};
